import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { filterTextParameterOptions } from '../../../shared/lib/revit/parameterOptionTypeFilter';
import {
  Box,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { CaptionText } from '../../../shared/ui/typography';
import {
  useProcessPropertiesConfiguration,
  useFormValidation,
  useParameterAddition,
} from '../../../shared/lib';
import { useUIStore } from '../../../shared/stores/useUIStore';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import {
  PROCESS_CONDITIONS_FIELDS,
  PROJECT_PROPERTIES_FIELDS,
  withCondition,
} from '../../../shared/config/validationConfig';
import {
  DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES,
  DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES,
  PROCESS_CONDITIONS_EDITOR_ROW_ID_BY_FIELD,
  PROJECT_PROPERTIES_EDITOR_ROW_ID_BY_FIELD,
} from '../../../shared/config/mappingScopeEditorFieldDefaults';
import { processPropertiesTooltips } from '../../../shared/config/wizardTooltips';
import MainLayout from '../../../shared/ui/layout/MainLayout';
import { layoutGap } from '../../../theme/theme';
import { OperationProgressWindowRequest } from '../../../shared/ui/progress';
import WizardSnackbarAlert from '../../../shared/ui/workflow/WizardSnackbarAlert';
import CompactFieldRow from '../../../shared/ui/workflow/CompactFieldRow';
import ParameterCreationConsentCard from '../../../shared/ui/workflow/ParameterCreationConsentCard';
import { useCreateRequiredParametersConfirmation } from '../lib/useCreateRequiredParametersConfirmation';
import { buildProcessPropertiesFooterContent } from '../lib/processPropertiesFooter';
import {
  ActiveSectionHeader,
  EditorSurface,
  MappingFieldGrid,
  type SectionSummaryItem,
  SectionSwitcher,
  SetupConfirmationStrip,
  WorkflowNotice,
  WorkflowStepShell,
} from '../../../shared/ui/workflow/WorkflowStepComponents';
import {
  resolveProcessDestinationFocus,
  ValidationNavigationNotice,
} from '../../pcf-validation';
import {
  PROCESS_CONDITIONS_EDITOR_STORAGE_KEY,
  PROJECT_PROPERTIES_EDITOR_STORAGE_KEY,
  loadMappingScopeEditorState,
} from '../../../shared/api/pipeex/mappingScopeEditorProfileState';
import { useActiveProjectKey } from '../../../shared/hooks/ActiveDocumentProvider';
import { useParameterDropdownOptions } from '../lib/useParameterDropdownOptions';

const debugProcessProperties = (_message: string, _payload?: unknown) => {};

interface ProcessPropertiesPageProps {
  onGoBack: () => void;
  onContinue: () => void;
}

type SectionKey = 'processConditions' | 'projectProperties';
type ProcessFieldKey =
  | 'processConditionsValue'
  | 'pipingSpecValuePC'
  | 'tracingSpecValuePC'
  | 'attribute12Value'
  | 'attribute13Value'
  | 'attribute14Value'
  | 'attribute15Value'
  | 'attribute16Value'
  | 'attribute17Value'
  | 'attribute18Value'
  | 'attribute19Value'
  | 'attribute20Value'
  | 'attribute21Value'
  | 'attribute22Value'
  | 'attribute23Value'
  | 'projectPropertiesValue'
  | 'projectIdentifierValue'
  | 'projectNameValue'
  | 'areaValue'
  | 'attribute1ClientName'
  | 'attribute2ProjectAddress'
  | 'attribute3ClientDepartment'
  | 'attribute4ClientProjectNumber'
  | 'attribute5ClientDocumentNumber'
  | 'attribute6ClientCode'
  | 'attribute7EngPartyOffice'
  | 'attribute8EngPartyOfficeLocation'
  | 'attribute9EngPartyProjectNumber'
  | 'attribute10EngPartyAreaCode';

interface FieldDefinition {
  field: ProcessFieldKey;
  label: string;
  required: boolean;
  help?: (typeof processPropertiesTooltips)[keyof typeof processPropertiesTooltips];
  focusTokens: string[];
}

interface SectionDefinition {
  key: SectionKey;
  title: string;
  shortTitle: string;
  description: string;
  toggleField: 'processConditionsChecked' | 'projectPropertiesChecked';
  fields: readonly FieldDefinition[];
}

const PROCESS_SECTION_FIELDS_DEF: readonly FieldDefinition[] = [
  { field: 'processConditionsValue', label: 'Process condition *', required: true, focusTokens: ['PROCESS-CONDITION'] },
  { field: 'pipingSpecValuePC', label: 'PIPING-SPEC *', required: true, focusTokens: ['PIPING-SPEC'] },
  { field: 'attribute12Value', label: 'ATTRIBUTE12 (Design pressure) *', required: true, focusTokens: ['ATTRIBUTE12'] },
  { field: 'attribute13Value', label: 'ATTRIBUTE13 (Design temperature) *', required: true, focusTokens: ['ATTRIBUTE13'] },
  { field: 'tracingSpecValuePC', label: 'TRACING-SPEC', required: false, focusTokens: ['TRACING-SPEC'] },
  { field: 'attribute14Value', label: 'ATTRIBUTE14 (Operating pressure)', required: false, focusTokens: ['ATTRIBUTE14'] },
  { field: 'attribute15Value', label: 'ATTRIBUTE15 (Operating temperature)', required: false, focusTokens: ['ATTRIBUTE15'] },
  { field: 'attribute16Value', label: 'ATTRIBUTE16 (Service description)', required: false, focusTokens: ['ATTRIBUTE16'] },
  { field: 'attribute17Value', label: 'ATTRIBUTE17 (PED group)', required: false, focusTokens: ['ATTRIBUTE17'] },
  { field: 'attribute18Value', label: 'ATTRIBUTE18 (PED category)', required: false, focusTokens: ['ATTRIBUTE18'] },
  { field: 'attribute19Value', label: 'ATTRIBUTE19 (Test pressure)', required: false, focusTokens: ['ATTRIBUTE19'] },
  { field: 'attribute20Value', label: 'ATTRIBUTE20 (Test type)', required: false, focusTokens: ['ATTRIBUTE20'] },
  { field: 'attribute21Value', label: 'ATTRIBUTE21 (Heat-tracing spec)', required: false, focusTokens: ['ATTRIBUTE21'] },
  { field: 'attribute22Value', label: 'ATTRIBUTE22 (Insulation thickness)', required: false, focusTokens: ['ATTRIBUTE22'] },
  { field: 'attribute23Value', label: 'ATTRIBUTE23 (Insulation type)', required: false, focusTokens: ['ATTRIBUTE23'] },
];

const PROJECT_SECTION_FIELDS_DEF: readonly FieldDefinition[] = [
  { field: 'projectPropertiesValue', label: 'Project property *', required: true, focusTokens: ['PROJECT-PROPERTY'] },
  { field: 'projectIdentifierValue', label: 'PROJECT-IDENTIFIER *', required: true, focusTokens: ['PROJECT-IDENTIFIER'] },
  { field: 'projectNameValue', label: 'Project name *', required: true, focusTokens: ['PROJECT-NAME'] },
  { field: 'areaValue', label: 'AREA', required: false, focusTokens: ['AREA'] },
  { field: 'attribute1ClientName', label: 'ATTRIBUTE1 (Client name)', required: false, focusTokens: ['ATTRIBUTE1'] },
  { field: 'attribute2ProjectAddress', label: 'ATTRIBUTE2 (Project address)', required: false, focusTokens: ['ATTRIBUTE2'] },
  { field: 'attribute3ClientDepartment', label: 'ATTRIBUTE3 (Client department)', required: false, focusTokens: ['ATTRIBUTE3'] },
  { field: 'attribute4ClientProjectNumber', label: 'ATTRIBUTE4 (Client project number)', required: false, focusTokens: ['ATTRIBUTE4'] },
  { field: 'attribute5ClientDocumentNumber', label: 'ATTRIBUTE5 (Client document number)', required: false, focusTokens: ['ATTRIBUTE5'] },
  { field: 'attribute6ClientCode', label: 'ATTRIBUTE6 (Client code)', required: false, focusTokens: ['ATTRIBUTE6'] },
  { field: 'attribute7EngPartyOffice', label: 'ATTRIBUTE7 (Engineering office)', required: false, focusTokens: ['ATTRIBUTE7'] },
  { field: 'attribute8EngPartyOfficeLocation', label: 'ATTRIBUTE8 (Engineering office location)', required: false, focusTokens: ['ATTRIBUTE8'] },
  { field: 'attribute9EngPartyProjectNumber', label: 'ATTRIBUTE9 (Engineering project number)', required: false, focusTokens: ['ATTRIBUTE9'] },
  { field: 'attribute10EngPartyAreaCode', label: 'ATTRIBUTE10 (Engineering area code)', required: false, focusTokens: ['ATTRIBUTE10'] },
];

const SECTION_DEFINITIONS: readonly SectionDefinition[] = [
  {
    key: 'processConditions',
    title: 'Process conditions',
    shortTitle: 'Process',
    description: 'Map design, operating, service, and compliance metadata to the corresponding PCF system fields.',
    toggleField: 'processConditionsChecked',
    fields: PROCESS_SECTION_FIELDS_DEF,
  },
  {
    key: 'projectProperties',
    title: 'Project properties',
    shortTitle: 'Project',
    description: 'Map project and client metadata to the PCF header.',
    toggleField: 'projectPropertiesChecked',
    fields: PROJECT_SECTION_FIELDS_DEF,
  },
];

interface PersistedLabelSource<TField extends ProcessFieldKey> {
  storageKey: string;
  defaultPcfAttributeNames: Record<string, string>;
  editorRowIdByField: Partial<Record<TField, string>>;
}

const loadPersistedFieldLabels = <TField extends ProcessFieldKey>({
  storageKey,
  defaultPcfAttributeNames,
  editorRowIdByField,
}: PersistedLabelSource<TField>): Partial<Record<TField, string>> => {
  const persistedState = loadMappingScopeEditorState(storageKey, defaultPcfAttributeNames);
  const fieldEntries = Object.entries(editorRowIdByField) as Array<[TField, string | undefined]>;

  return fieldEntries.reduce<Partial<Record<TField, string>>>(
    (labels, [field, rowId]) => {
      if (!rowId) {
        return labels;
      }

      const nextLabel = persistedState.pcfAttributeNames[rowId] ?? defaultPcfAttributeNames[rowId];
      if (typeof nextLabel === 'string' && nextLabel.trim() !== '') {
        labels[field as TField] = nextLabel;
      }
      return labels;
    },
    {}
  );
};

const applyPersistedLabels = <TField extends ProcessFieldKey>(
  fields: readonly FieldDefinition[],
  persistedLabels: Partial<Record<TField, string>>
): FieldDefinition[] =>
  fields.map((field) => {
    const persistedLabel = persistedLabels[field.field as TField];

    return {
      ...field,
      label: persistedLabel ? `${persistedLabel}${field.required ? ' *' : ''}` : field.label,
    };
  });

const PROCESS_CONDITIONS_LABEL_SOURCE: PersistedLabelSource<ProcessFieldKey> = {
  storageKey: PROCESS_CONDITIONS_EDITOR_STORAGE_KEY,
  defaultPcfAttributeNames: DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES,
  editorRowIdByField:
    PROCESS_CONDITIONS_EDITOR_ROW_ID_BY_FIELD as Partial<Record<ProcessFieldKey, string>>,
};

const PROJECT_PROPERTIES_LABEL_SOURCE: PersistedLabelSource<ProcessFieldKey> = {
  storageKey: PROJECT_PROPERTIES_EDITOR_STORAGE_KEY,
  defaultPcfAttributeNames: DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES,
  editorRowIdByField:
    PROJECT_PROPERTIES_EDITOR_ROW_ID_BY_FIELD as Partial<Record<ProcessFieldKey, string>>,
};

const FIELD_SECTION_LOOKUP: Record<ProcessFieldKey, SectionKey> = {
  processConditionsValue: 'processConditions',
  pipingSpecValuePC: 'processConditions',
  tracingSpecValuePC: 'processConditions',
  attribute12Value: 'processConditions',
  attribute13Value: 'processConditions',
  attribute14Value: 'processConditions',
  attribute15Value: 'processConditions',
  attribute16Value: 'processConditions',
  attribute17Value: 'processConditions',
  attribute18Value: 'processConditions',
  attribute19Value: 'processConditions',
  attribute20Value: 'processConditions',
  attribute21Value: 'processConditions',
  attribute22Value: 'processConditions',
  attribute23Value: 'processConditions',
  projectPropertiesValue: 'projectProperties',
  projectIdentifierValue: 'projectProperties',
  projectNameValue: 'projectProperties',
  areaValue: 'projectProperties',
  attribute1ClientName: 'projectProperties',
  attribute2ProjectAddress: 'projectProperties',
  attribute3ClientDepartment: 'projectProperties',
  attribute4ClientProjectNumber: 'projectProperties',
  attribute5ClientDocumentNumber: 'projectProperties',
  attribute6ClientCode: 'projectProperties',
  attribute7EngPartyOffice: 'projectProperties',
  attribute8EngPartyOfficeLocation: 'projectProperties',
  attribute9EngPartyProjectNumber: 'projectProperties',
  attribute10EngPartyAreaCode: 'projectProperties',
};

const shouldLogProcessPropertiesLabels = process.env.NODE_ENV !== 'test';

const ProcessPropertiesPage: React.FC<ProcessPropertiesPageProps> = ({
  onGoBack,
  onContinue,
}) => {
  const { config, updateField } = useProcessPropertiesConfiguration();
  const [createParametersConfirmed, setCreateParametersConfirmed] = useState(false);
  const activeSection = useUIStore((state) => state.sections.processProperties);
  const setActiveSection = useUIStore((state) => state.setProcessPropertiesSection);
  const issueNavigationContext = usePcfValidationStore((state) => state.issueNavigationContext);
  const activeProjectKey = useActiveProjectKey();
  const { options: dropdownParameterOptions } = useParameterDropdownOptions(activeProjectKey);
  const textParameterOptions = useMemo(
    () => filterTextParameterOptions(dropdownParameterOptions),
    [dropdownParameterOptions]
  );
  const [notification, setNotification] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const processConditionsChecked = config.processConditionsChecked;
  const projectPropertiesChecked = config.projectPropertiesChecked;
  const hasEnabledSections = processConditionsChecked || projectPropertiesChecked;
  const processConditionsFieldLabels = useMemo(
    () => loadPersistedFieldLabels(PROCESS_CONDITIONS_LABEL_SOURCE),
    []
  );
  const projectPropertiesFieldLabels = useMemo(
    () => loadPersistedFieldLabels(PROJECT_PROPERTIES_LABEL_SOURCE),
    []
  );

  const processSectionFields = useMemo(
    () => applyPersistedLabels(PROCESS_SECTION_FIELDS_DEF, processConditionsFieldLabels),
    [processConditionsFieldLabels]
  );

  const projectSectionFields = useMemo(
    () => applyPersistedLabels(PROJECT_SECTION_FIELDS_DEF, projectPropertiesFieldLabels),
    [projectPropertiesFieldLabels]
  );

  const sectionDefinitions = useMemo(
    () => [
      {
        ...SECTION_DEFINITIONS[0],
        fields: processSectionFields,
      },
      {
        ...SECTION_DEFINITIONS[1],
        fields: projectSectionFields,
      },
    ] as const,
    [processSectionFields, projectSectionFields]
  );

  useEffect(() => {
    debugProcessProperties('[ProcessPropertiesPage] Flags changed', {
      processConditionsChecked,
      projectPropertiesChecked,
    });
  }, [processConditionsChecked, projectPropertiesChecked]);

  useEffect(() => {
    if (!shouldLogProcessPropertiesLabels) {
      return;
    }

    debugProcessProperties('[ProcessPropertiesPage] Scope editor labels', {
      processConditionsLabels: processSectionFields.map((field) => field.label),
      projectPropertiesLabels: projectSectionFields.map((field) => field.label),
    });
  }, [processSectionFields, projectSectionFields]);

  const enabledSections = useMemo(
    () => sectionDefinitions.filter((section) =>
      section.key === 'processConditions' ? processConditionsChecked : projectPropertiesChecked
    ),
    [processConditionsChecked, projectPropertiesChecked, sectionDefinitions]
  );

  useEffect(() => {
    if (enabledSections.length === 0) return;
    const activeExists = enabledSections.some((section) => section.key === activeSection);
    if (!activeExists) {
      setActiveSection(enabledSections[0].key);
    }
  }, [activeSection, enabledSections, setActiveSection]);

  const validationFields = useMemo(
    () => [
      ...withCondition(PROCESS_CONDITIONS_FIELDS, () => processConditionsChecked),
      ...withCondition(PROJECT_PROPERTIES_FIELDS, () => projectPropertiesChecked),
    ],
    [processConditionsChecked, projectPropertiesChecked]
  );

  const {
    validation,
    getFieldError,
    markFieldTouched,
    showValidationErrors,
    setShowValidationErrors,
  } = useFormValidation({
    config,
    fields: validationFields,
  });

  const {
    isAdding: isAddingParameters,
    parameterProgress,
    addProcessAndProjectParameters,
  } = useParameterAddition();
  const validationFocusFields = useMemo(
    () =>
      new Set(
        (issueNavigationContext?.actionParams?.processFields || issueNavigationContext?.affectedItems || [])
          .map((item) => item.trim().toUpperCase().replace(/[\s_]+/g, '-'))
          .filter(Boolean)
      ),
    [issueNavigationContext?.actionParams?.processFields, issueNavigationContext?.affectedItems]
  );

  const isValidationFocused = useCallback(
    (...tokens: string[]) =>
      tokens.some((token) => validationFocusFields.has(token.trim().toUpperCase().replace(/[\s_]+/g, '-'))),
    [validationFocusFields]
  );

  const createFieldHandler = useCallback(
    (fieldName: ProcessFieldKey) => (value: string) => {
      updateField(fieldName, value);
      markFieldTouched(fieldName);
    },
    [markFieldTouched, updateField]
  );

  const fieldHandlers = useMemo<Record<ProcessFieldKey, (value: string) => void>>(
    () => ({
      processConditionsValue: createFieldHandler('processConditionsValue'),
      pipingSpecValuePC: createFieldHandler('pipingSpecValuePC'),
      tracingSpecValuePC: createFieldHandler('tracingSpecValuePC'),
      attribute12Value: createFieldHandler('attribute12Value'),
      attribute13Value: createFieldHandler('attribute13Value'),
      attribute14Value: createFieldHandler('attribute14Value'),
      attribute15Value: createFieldHandler('attribute15Value'),
      attribute16Value: createFieldHandler('attribute16Value'),
      attribute17Value: createFieldHandler('attribute17Value'),
      attribute18Value: createFieldHandler('attribute18Value'),
      attribute19Value: createFieldHandler('attribute19Value'),
      attribute20Value: createFieldHandler('attribute20Value'),
      attribute21Value: createFieldHandler('attribute21Value'),
      attribute22Value: createFieldHandler('attribute22Value'),
      attribute23Value: createFieldHandler('attribute23Value'),
      projectPropertiesValue: createFieldHandler('projectPropertiesValue'),
      projectIdentifierValue: createFieldHandler('projectIdentifierValue'),
      projectNameValue: createFieldHandler('projectNameValue'),
      areaValue: createFieldHandler('areaValue'),
      attribute1ClientName: createFieldHandler('attribute1ClientName'),
      attribute2ProjectAddress: createFieldHandler('attribute2ProjectAddress'),
      attribute3ClientDepartment: createFieldHandler('attribute3ClientDepartment'),
      attribute4ClientProjectNumber: createFieldHandler('attribute4ClientProjectNumber'),
      attribute5ClientDocumentNumber: createFieldHandler('attribute5ClientDocumentNumber'),
      attribute6ClientCode: createFieldHandler('attribute6ClientCode'),
      attribute7EngPartyOffice: createFieldHandler('attribute7EngPartyOffice'),
      attribute8EngPartyOfficeLocation: createFieldHandler('attribute8EngPartyOfficeLocation'),
      attribute9EngPartyProjectNumber: createFieldHandler('attribute9EngPartyProjectNumber'),
      attribute10EngPartyAreaCode: createFieldHandler('attribute10EngPartyAreaCode'),
    }),
    [createFieldHandler]
  );

  const sectionStates = useMemo(() =>
    sectionDefinitions.map((section) => {
      const enabled = section.key === 'processConditions' ? processConditionsChecked : projectPropertiesChecked;
      const requiredFields = section.fields.filter((f) => f.required);
      const requiredComplete = requiredFields.filter((f) => (config[f.field].value || '').trim()).length;
      const configuredCount = section.fields.filter((f) => (config[f.field].value || '').trim()).length;
      const missingRequiredFields = enabled
        ? requiredFields.filter((f) => !(config[f.field].value || '').trim())
        : [];
      const errorMessages = enabled
        ? section.fields.map((f) => validation.errors[f.field]).filter((m): m is string => Boolean(m))
        : [];
      const hasBlockingIssue = enabled && (missingRequiredFields.length > 0 || errorMessages.length > 0);

      return {
        ...section,
        enabled,
        requiredCount: requiredFields.length,
        requiredComplete,
        configuredCount,
        missingRequiredFields,
        errorMessages,
        hasBlockingIssue,
        isComplete: enabled && missingRequiredFields.length === 0 && configuredCount > 0,
      };
    }),
    [config, processConditionsChecked, projectPropertiesChecked, sectionDefinitions, validation.errors]
  );

  const activeSectionState = useMemo(
    () => sectionStates.find((s) => s.key === activeSection) ?? sectionStates[0],
    [activeSection, sectionStates]
  );
  const overallProgress = useMemo(() => {
    const enabledSectionsList = sectionStates.filter(s => s.enabled);
    const totalRequired = enabledSectionsList.reduce((sum, s) => sum + s.requiredCount, 0);
    const completedRequired = enabledSectionsList.reduce((sum, s) => sum + s.requiredComplete, 0);
    return {
      totalRequired,
      completedRequired,
      percent: totalRequired > 0 ? Math.round((completedRequired / totalRequired) * 100) : 0,
    };
  }, [sectionStates]);

  const blockingReasons = useMemo(() => {
    const reasons: string[] = [];
    if (!hasEnabledSections) reasons.push('select at least one metadata group');
    if (!validation.isValid) {
      const firstError = sectionStates.find(s => s.enabled && s.errorMessages.length > 0)?.errorMessages[0];
      if (firstError) {
        reasons.push(firstError);
      } else if (validation.missingFields.length > 0) {
        reasons.push(`${validation.missingFields.length} required mapping${validation.missingFields.length === 1 ? '' : 's'} missing`);
      }
    }
    return reasons;
  }, [hasEnabledSections, sectionStates, validation.isValid, validation.missingFields.length]);

  const footerContent = useMemo(
    () => buildProcessPropertiesFooterContent({ blockingReasons, ...overallProgress }),
    [blockingReasons, overallProgress]
  );

  const { requestDefaultPropertiesChange, confirmationDialog: createParametersConfirmationDialog } =
    useCreateRequiredParametersConfirmation(setCreateParametersConfirmed, {
      destination: 'project',
      bindingKind: 'instance',
    });

  const handleToggleSection = useCallback(
    (field: 'processConditionsChecked' | 'projectPropertiesChecked') =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        debugProcessProperties('[ProcessPropertiesPage] Toggle section', {
          field,
          nextChecked: event.target.checked,
          processConditionsChecked,
          projectPropertiesChecked,
        });
        updateField(field, event.target.checked);
      },
    [processConditionsChecked, projectPropertiesChecked, updateField]
  );

  const handleContinue = useCallback(async () => {
    debugProcessProperties('[ProcessPropertiesPage] Continue requested', {
      processConditionsChecked,
      projectPropertiesChecked,
      createParameters: createParametersConfirmed,
      validationIsValid: validation.isValid,
      missingFields: validation.missingFields,
    });
    if (!hasEnabledSections) {
      setNotification({
        open: true,
         message: 'Select at least one metadata group before continuing.',
      });
      return;
    }

    if (!validation.isValid) {
      setShowValidationErrors(true);
      const invalidField = Object.keys(validation.errors)[0] as ProcessFieldKey | undefined;
      if (invalidField && FIELD_SECTION_LOOKUP[invalidField]) {
        setActiveSection(FIELD_SECTION_LOOKUP[invalidField]);
      }
      setNotification({
        open: true,
         message: `Complete the required property mappings: ${validation.missingFields.join(', ')}`,
      });
      return;
    }

    if (createParametersConfirmed) {
      const parameterSetupResult = await addProcessAndProjectParameters();
      if (!parameterSetupResult.success) {
        setNotification({
          open: true,
          message:
            parameterSetupResult.error ||
            'Some selected process or project parameters could not be added to the active Revit project.',
        });
        return;
      }
    }
    debugProcessProperties('[ProcessPropertiesPage] Continue committing navigation', {
      processConditionsChecked,
      projectPropertiesChecked,
    });
    onContinue();
  }, [
    addProcessAndProjectParameters,
    createParametersConfirmed,
    hasEnabledSections,
    onContinue,
    processConditionsChecked,
    projectPropertiesChecked,
    setActiveSection,
    setShowValidationErrors,
    validation.errors,
    validation.isValid,
    validation.missingFields,
  ]);

  const handleNotificationClose = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    if (issueNavigationContext?.targetPage !== 'properties') return;
    const focusedItems = issueNavigationContext.actionParams?.processFields || issueNavigationContext.affectedItems;
    const nextFocus = issueNavigationContext.actionParams?.processSections
      ? {
          processConditions: issueNavigationContext.actionParams.processSections.includes('processConditions'),
          projectProperties: issueNavigationContext.actionParams.processSections.includes('projectProperties'),
        }
      : resolveProcessDestinationFocus(focusedItems);

    if (nextFocus.projectProperties && projectPropertiesChecked) {
      setActiveSection('projectProperties');
    } else if (nextFocus.processConditions && processConditionsChecked) {
      setActiveSection('processConditions');
    }
  }, [
    issueNavigationContext?.familyId,
    issueNavigationContext?.targetPage,
    issueNavigationContext?.affectedItems,
    issueNavigationContext?.actionParams?.processFields,
    issueNavigationContext?.actionParams?.processSections,
    processConditionsChecked,
    projectPropertiesChecked,
    setActiveSection,
  ]);

  const canProceed = hasEnabledSections && validation.isValid && !isAddingParameters;
  const enabledSectionStates = sectionStates.filter((section) => section.enabled);
  const sectionSummaryItems = useMemo<SectionSummaryItem[]>(
    () =>
      enabledSectionStates.map((section) => {
        const statusTone: SectionSummaryItem['statusTone'] = section.isComplete
          ? 'ready'
          : section.hasBlockingIssue
            ? 'missing'
            : 'optional';

        return {
        key: section.key,
        title: section.shortTitle,
        detail: section.description,
        statusLabel: section.isComplete
          ? 'Ready'
          : section.hasBlockingIssue
            ? 'Needs attention'
            : `${section.configuredCount}/${section.fields.length} set`,
        statusTone,
        selected: activeSection === section.key,
        onClick: () => setActiveSection(section.key),
        role: 'tab',
        ariaSelected: activeSection === section.key,
        ariaControls: `process-properties-panel-${section.key}`,
        id: `process-properties-tab-${section.key}`,
        tabIndex: activeSection === section.key ? 0 : -1,
      };
    }),
    [activeSection, enabledSectionStates, setActiveSection]
  );
  const activeSectionStatusLabel = activeSectionState.isComplete
    ? 'Ready'
    : activeSectionState.hasBlockingIssue
      ? 'Needs attention'
      : `${activeSectionState.configuredCount}/${activeSectionState.fields.length} set`;
  const activeSectionStatusTone = activeSectionState.isComplete
    ? 'ready'
    : activeSectionState.hasBlockingIssue
      ? 'missing'
      : 'optional';

  return (
    <>
      <OperationProgressWindowRequest
        id="workflow-process-property-parameter-addition"
        open={isAddingParameters}
        priority={100}
        model={parameterProgress ?? {
          title: 'Creating process and project parameters',
          message: 'Revit is creating the selected missing parameters.',
          detail: 'Existing compatible parameters and values remain unchanged; missing INSTANCE bindings are created.',
          status: 'running',
          percent: null,
          locked: true,
        }}
      />

      {createParametersConfirmationDialog}
      <MainLayout
        title="Properties"
        maxWidth={960}
        maxHeight="92vh"
        showBack
        onBack={onGoBack}
        showNext
        onNext={handleContinue}
        nextText={isAddingParameters ? 'Adding…' : 'Continue'}
        nextDisabled={!canProceed}
        compactWorkflowChrome
        workflowChromeVariant="wizard"
        showRuntimeBadgeInUtilityChrome={false}
        footerContent={footerContent}
      >
        <ValidationNavigationNotice applicableTargetPages={['properties']} />

        <WorkflowStepShell sx={{ width: '100%', py: 0.75 }}>
          {showValidationErrors && !validation.isValid && (
            <WorkflowNotice severity="warning" title="Complete the required property mappings">
              <CaptionText>
                Missing: {validation.missingFields.join(', ')}
              </CaptionText>
            </WorkflowNotice>
          )}

          <SetupConfirmationStrip
            eyebrow=""
            title="Choose metadata for the PCF profile"
            description="Select the process and project metadata groups to include in this PCF profile. Creating any missing Revit parameters is optional and occurs only after you confirm the selection."
            statusLabel={
              hasEnabledSections
                ? `${enabledSectionStates.length} group${enabledSectionStates.length === 1 ? '' : 's'} active`
                 : 'No groups selected'
            }
            statusTone={hasEnabledSections ? 'set' : 'missing'}
            blocker={!hasEnabledSections ? 'Continue blocked: select at least one metadata group' : null}
            control={
              <Box
                sx={{
                  display: 'grid',
                  gap: layoutGap.row,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(processConditionsChecked)}
                        onChange={handleToggleSection('processConditionsChecked')}
                        size="small"
                        sx={{ p: 0.4, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                      />
                    }
                    label={
                      <CaptionText>
                        Process conditions
                      </CaptionText>
                    }
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(projectPropertiesChecked)}
                        onChange={handleToggleSection('projectPropertiesChecked')}
                        size="small"
                        sx={{ p: 0.4, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                      />
                    }
                    label={
                      <CaptionText>
                        Project properties
                      </CaptionText>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
                <ParameterCreationConsentCard
                  checked={createParametersConfirmed}
                  requiredToContinue={false}
                  onChange={(checked) => void requestDefaultPropertiesChange(checked)}
                />
              </Box>
            }
          />

          {hasEnabledSections ? (
            <Box sx={{ display: 'grid', gap: layoutGap.row, minWidth: 0 }}>
              {enabledSectionStates.length > 1 && (
                <SectionSwitcher
                  ariaLabel="Process property sections"
                  items={sectionSummaryItems}
                />
              )}

              <EditorSurface sx={{ p: 1, minWidth: 0 }}>
                <Box
                  role="tabpanel"
                  id={`process-properties-panel-${activeSectionState.key}`}
                  aria-labelledby={`process-properties-tab-${activeSectionState.key}`}
                  sx={{ display: 'grid', gap: layoutGap.row }}
                >
                  <ActiveSectionHeader
                    title={activeSectionState.title}
                    description={activeSectionState.description}
                    statusLabel={activeSectionStatusLabel}
                    statusTone={activeSectionStatusTone}
                  />

                  {activeSectionState.hasBlockingIssue && (
                    <WorkflowNotice severity="warning">
                      {activeSectionState.missingRequiredFields.length > 0
                        ? `${activeSectionState.missingRequiredFields.length} required field${activeSectionState.missingRequiredFields.length === 1 ? '' : 's'} missing`
                        : activeSectionState.errorMessages[0]}
                    </WorkflowNotice>
                  )}

                  <MappingFieldGrid columns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}>
                    {activeSectionState.fields.map((field) => (
                      <CompactFieldRow
                        key={field.field}
                        label={field.label.replace(' *', '')}
                        value={config[field.field].value}
                        onChange={fieldHandlers[field.field]}
                        options={textParameterOptions}
                        help={field.help}
                        error={getFieldError(field.field)}
                        validationFocused={isValidationFocused(...field.focusTokens)}
                        required={field.required}
                      />
                    ))}
                  </MappingFieldGrid>
                </Box>
              </EditorSurface>
            </Box>
          ) : (
            <WorkflowNotice severity="info">
              Select at least one metadata group to map process conditions or project properties.
            </WorkflowNotice>
          )}

          {textParameterOptions.length === 0 && (
            <WorkflowNotice severity="info">
              Load shared parameter definitions to browse available names, or type exact Revit parameter names.
            </WorkflowNotice>
          )}
        </WorkflowStepShell>
      </MainLayout>

      <WizardSnackbarAlert
        open={notification.open}
        onClose={handleNotificationClose}
        severity="warning"
        title="Property mappings required"
      >
        {notification.message}
      </WizardSnackbarAlert>
    </>
  );
};

export default ProcessPropertiesPage;
